import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { ImageIcon, UploadCloud } from 'lucide-react';
import {
  acceptAttributeFor,
  buildStorefrontMediaProxyUrl,
  precheckWebsiteMediaFile,
  useUploadWebsiteMedia,
  websiteTemplateThumbnailSrc,
  type WebsiteMediaKind,
  type WebsiteMediaUploadProgress,
} from '@dukanti/shared';
import { env } from '@dukanti/shared/env';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';
import { useWebsiteT } from '../../hooks/use-website-t';
import { captureMediaPoster } from '../../lib/capture-video-poster';
import { mediaPrecheckErrorKey, mediaUploadErrorKey } from '../../lib/issue-messages';
import { authoredMedia, hasTemplateDefault } from '../../lib/slot-values';
import type { SlotFieldProps } from './slot-text-field';

export interface MediaUploadFieldProps extends SlotFieldProps {
  readonly storeId: string;
  /** The field tells the dialog while a file is on its way: Save waits. */
  readonly onUploadingChange: (uploading: boolean) => void;
  /** A test seam: the default reads a frame in this browser. */
  readonly capturePoster?: (file: File) => Promise<File>;
  /**
   * Epic #228 P5: "Remove" is offered when the element's capabilities say the
   * media is removable (`!required`, the same truth). Defaults to the schema.
   */
  readonly removable?: boolean;
  /**
   * Epic #228 P6: what stands in for the "current media" thumbnail (the crop
   * editor in its true frame). Without it, the plain thumbnail.
   */
  readonly preview?: ReactNode;
}

type Phase = 'idle' | WebsiteMediaUploadProgress['phase'];

const LIMIT_KEYS: Readonly<Record<WebsiteMediaKind, string>> = {
  image: 'admin.websiteTemplates.media.limits.image',
  gif: 'admin.websiteTemplates.media.limits.gif',
  video: 'admin.websiteTemplates.media.limits.video',
};

function fileOf(list: FileList | null | undefined): File | null {
  return list && list.length > 0 ? (list[0] ?? null) : null;
}

/**
 * The media field of a region (Screen 4): the current media, an upload target
 * that takes a click or a dropped file, the limits, progress, and the way
 * back to the template's image. Only media, its cover and its description.
 *
 * Every file is checked here for type, name and size before a byte leaves
 * (the server decides again). A video or an animated GIF gets a cover from
 * its own frame; where this browser cannot make one, the merchant uploads it.
 */
export function MediaUploadField({
  slot,
  state,
  onChange,
  issueKey,
  storeId,
  onUploadingChange,
  capturePoster = captureMediaPoster,
  removable = !slot.required,
  preview,
}: MediaUploadFieldProps): React.JSX.Element {
  const { t } = useWebsiteT();
  const id = useId();
  const supabaseUrl = env.VITE_SUPABASE_URL;
  const upload = useUploadWebsiteMedia(supabase, { storeId, supabaseUrl });
  const kinds = slot.mediaKinds ?? ['image'];
  const media = authoredMedia(state);

  const [phase, setPhase] = useState<Phase>('idle');
  const [fraction, setFraction] = useState(0);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [posterNote, setPosterNote] = useState<'made' | null>(null);
  const [dragging, setDragging] = useState(false);
  const controllerRef = useRef<AbortController | null>(null);
  const onUploadingRef = useRef(onUploadingChange);
  onUploadingRef.current = onUploadingChange;

  // The dialog closing (or the field going away) stops a file on its way.
  useEffect(
    () => () => {
      controllerRef.current?.abort();
    },
    [],
  );

  const busy = phase !== 'idle';

  const run = async <T,>(task: (signal: AbortSignal) => Promise<T>): Promise<T | null> => {
    const controller = new AbortController();
    controllerRef.current = controller;
    setErrorKey(null);
    setPhase('uploading');
    setFraction(0);
    onUploadingRef.current(true);
    try {
      return await task(controller.signal);
    } catch (error) {
      setErrorKey(mediaUploadErrorKey(error));
      return null;
    } finally {
      if (controllerRef.current === controller) controllerRef.current = null;
      setPhase('idle');
      onUploadingRef.current(false);
    }
  };

  const onProgress = (progress: WebsiteMediaUploadProgress): void => {
    setPhase(progress.phase);
    setFraction(progress.fraction);
  };

  const handleMedia = async (file: File): Promise<void> => {
    const problem = precheckWebsiteMediaFile(file, slot);
    if (problem !== null) {
      setErrorKey(mediaPrecheckErrorKey(problem));
      return;
    }
    const alt = media?.alt ?? '';
    await run(async (signal) => {
      const uploaded = await upload.mutateAsync({
        file,
        allowedKinds: kinds,
        altText: alt,
        onProgress,
        signal,
      });
      let posterAssetId: string | null = null;
      setPosterNote(null);
      if (uploaded.kind !== 'image') {
        try {
          const poster = await capturePoster(file);
          const stored = await upload.mutateAsync({
            file: poster,
            allowedKinds: ['image'],
            onProgress,
            signal,
          });
          posterAssetId = stored.mediaAssetId;
          setPosterNote('made');
        } catch (error) {
          if (signal.aborted) throw error;
          // This browser cannot read the file: the value stays without a
          // cover, its issue says so, and the merchant uploads one.
          posterAssetId = null;
        }
      }
      onChange({
        mode: 'authored',
        value: {
          v: { mediaAssetId: uploaded.mediaAssetId, kind: uploaded.kind, posterAssetId, alt },
        },
      });
    });
  };

  const handlePoster = async (file: File): Promise<void> => {
    if (!media) return;
    const problem = precheckWebsiteMediaFile(file, { ...slot, mediaKinds: ['image'] });
    if (problem !== null) {
      setErrorKey(mediaPrecheckErrorKey(problem));
      return;
    }
    await run(async (signal) => {
      const stored = await upload.mutateAsync({
        file,
        allowedKinds: ['image'],
        onProgress,
        signal,
      });
      setPosterNote(null);
      onChange({
        mode: 'authored',
        value: { v: { ...media, posterAssetId: stored.mediaAssetId } },
      });
    });
  };

  const previewSrc = ((): string | null => {
    if (media) {
      const shown = media.kind === 'image' ? media.mediaAssetId : media.posterAssetId;
      return shown ? buildStorefrontMediaProxyUrl(supabaseUrl, shown) : null;
    }
    if (state.mode === 'default' && slot.defaults.kind === 'template-media') {
      return websiteTemplateThumbnailSrc(slot.defaults.assetKey, supabaseUrl);
    }
    return null;
  })();

  const onlyImages = kinds.every((kind) => kind === 'image');
  const posterMissing = media !== null && media.kind !== 'image' && media.posterAssetId === null;
  const altMissing = media !== null && !slot.decorative && media.alt.trim() === '';

  return (
    <div className="space-y-3" data-testid={`field-${slot.id}`}>
      {preview ?? (
        <div className="space-y-1.5">
          <Label>{t('admin.websiteTemplates.media.current')}</Label>
          <div className="border-border bg-admin-surface-muted flex aspect-video max-h-48 items-center justify-center overflow-hidden rounded-lg border">
            {previewSrc === null ? (
              <span className="text-muted-foreground flex flex-col items-center gap-1 text-xs">
                <ImageIcon aria-hidden="true" className="size-6" />
                {t('admin.websiteTemplates.media.emptyPreview')}
              </span>
            ) : (
              <img
                src={previewSrc}
                alt={media?.alt ?? t('admin.websiteTemplates.media.current')}
                className="size-full object-contain"
                data-testid="media-preview"
              />
            )}
          </div>
        </div>
      )}

      <div
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          const file = fileOf(event.dataTransfer.files);
          if (file && !busy) void handleMedia(file);
        }}
        className={cn(
          'border-input rounded-lg border-2 border-dashed p-4 text-center transition-colors',
          dragging && 'border-admin-lime bg-admin-lime-light',
        )}
      >
        <label
          htmlFor={id}
          className={cn(
            'text-foreground inline-flex cursor-pointer items-center gap-2 text-sm font-medium',
            busy && 'pointer-events-none opacity-50',
          )}
        >
          <UploadCloud aria-hidden="true" className="size-5" />
          {onlyImages
            ? t('admin.websiteTemplates.media.uploadImage')
            : t('admin.websiteTemplates.media.uploadImageOrVideo')}
        </label>
        <input
          id={id}
          type="file"
          className="sr-only"
          accept={acceptAttributeFor(kinds)}
          disabled={busy}
          data-testid="media-file-input"
          onChange={(event) => {
            const file = fileOf(event.target.files);
            // The same file may be chosen again after an error.
            event.target.value = '';
            if (file) void handleMedia(file);
          }}
        />
        <p className="text-muted-foreground mt-1 text-xs">
          {t('admin.websiteTemplates.media.dropHint')}
        </p>
        <p className="text-muted-foreground mt-2 text-xs">
          {onlyImages
            ? t('admin.websiteTemplates.media.help.image')
            : t('admin.websiteTemplates.media.help.imageOrVideo')}
        </p>
        <ul className="text-muted-foreground mt-1 space-y-0.5 text-xs">
          {kinds.map((kind) => (
            <li key={kind}>{t(LIMIT_KEYS[kind])}</li>
          ))}
        </ul>
      </div>

      {busy ? (
        <div className="space-y-1" data-testid="media-progress">
          <div
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(fraction * 100)}
            aria-label={t('admin.websiteTemplates.media.uploading')}
            className="bg-muted h-2 overflow-hidden rounded-full"
          >
            <div
              className="bg-admin-lime h-full transition-all"
              style={{ width: `${Math.round(fraction * 100)}%` }}
            />
          </div>
          <p role="status" className="text-muted-foreground text-xs">
            {phase === 'checking'
              ? t('admin.websiteTemplates.media.checking')
              : t('admin.websiteTemplates.media.progress', { percent: Math.round(fraction * 100) })}
          </p>
        </div>
      ) : null}

      {errorKey === null ? null : (
        <p role="alert" data-testid="media-error" className="text-destructive text-xs">
          {t(errorKey)}
        </p>
      )}

      {media !== null && media.kind !== 'image' ? (
        <div className="space-y-1" data-testid="media-poster">
          <Label htmlFor={`${id}-poster`}>{t('admin.websiteTemplates.media.poster')}</Label>
          <p className="text-muted-foreground text-xs">
            {posterMissing
              ? t('admin.websiteTemplates.media.posterFailed')
              : posterNote === 'made'
                ? t('admin.websiteTemplates.media.posterAuto')
                : t('admin.websiteTemplates.media.posterHelp')}
          </p>
          <input
            id={`${id}-poster`}
            type="file"
            className="sr-only"
            accept={acceptAttributeFor(['image'])}
            disabled={busy}
            data-testid="poster-file-input"
            onChange={(event) => {
              const file = fileOf(event.target.files);
              event.target.value = '';
              if (file) void handlePoster(file);
            }}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={() => document.getElementById(`${id}-poster`)?.click()}
          >
            {t('admin.websiteTemplates.media.replacePoster')}
          </Button>
        </div>
      ) : null}

      {media !== null ? (
        <div className="space-y-1.5">
          <Label htmlFor={`${id}-alt`}>{t('admin.websiteTemplates.media.alt')}</Label>
          <Input
            id={`${id}-alt`}
            dir="auto"
            disabled={busy}
            value={media.alt}
            onChange={(event) =>
              onChange({ mode: 'authored', value: { v: { ...media, alt: event.target.value } } })
            }
          />
          {altMissing ? (
            <p className="text-muted-foreground text-xs" data-testid="media-alt-required">
              {t('admin.websiteTemplates.media.altRequired')}
            </p>
          ) : null}
        </div>
      ) : null}

      {issueKey === undefined || altMissing ? null : (
        <p role="alert" className="text-destructive text-xs">
          {t(issueKey)}
        </p>
      )}

      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {hasTemplateDefault(slot) && state.mode !== 'default' ? (
          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-auto p-0"
            disabled={busy}
            onClick={() => onChange({ mode: 'default' })}
          >
            {t('admin.websiteTemplates.media.useTemplateImage')}
          </Button>
        ) : null}
        {removable && state.mode !== 'empty' && (media !== null || state.mode === 'default') ? (
          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-auto p-0"
            disabled={busy}
            onClick={() => onChange({ mode: 'empty' })}
          >
            {t('admin.websiteTemplates.media.remove')}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
